import { useCallback, useEffect, useRef, useState } from "react";
import { API, api, getToken } from "@/lib/api";

const WS_URL = () =>
  `${API.replace(/^http/, "ws")}/ws/desk?token=${encodeURIComponent(getToken())}`;

/* Live desk feed. The server pushes positions / orders / summary / ticks over a WebSocket
   whenever they change; REST is used only for the first paint and as a fallback while the
   socket is down. Placing and modifying trades stays on REST. */
export const useDesk = () => {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [ticks, setTicks] = useState({});
  const [live, setLive] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const wsRef = useRef(null);
  const watchRef = useRef([]);
  const pollRef = useRef(null);
  const retryRef = useRef(null);

  const loadLive = useCallback(async (quiet = false) => {
    if (!quiet) setRefreshing(true);
    try {
      const [pos, sum, st, ord] = await Promise.all([
        api.get("/positions"),
        api.get("/summary"),
        api.get("/mt5/status"),
        api.get("/orders"),
      ]);
      setPositions(pos.data);
      setSummary(sum.data);
      setStatus(st.data);
      setOrders(ord.data);
      setUpdatedAt(Date.now());
    } catch {}
    if (!quiet) setRefreshing(false);
  }, []);

  const loadStatic = useCallback(async () => {
    try {
      const [s, sy] = await Promise.all([api.get("/settings/effective"), api.get("/symbols")]);
      setSettings(s.data);
      setSymbols(sy.data);
    } catch {}
  }, []);

  const watch = useCallback((list) => {
    watchRef.current = (list || []).filter(Boolean);
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN)
      ws.send(JSON.stringify({ type: "watch", symbols: watchRef.current }));
  }, []);

  useEffect(() => {
    loadStatic();
    loadLive();
    let closed = false;

    const stopPoll = () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
    const startPoll = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(loadLive, 5000);
    };

    const connect = () => {
      if (closed) return;
      let ws;
      try {
        ws = new WebSocket(WS_URL());
      } catch {
        retryRef.current = setTimeout(connect, 3000);
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (closed) {
          ws.close();
          return;
        }
        wsRef.current = ws;
        setLive(true);
        stopPoll();
        if (watchRef.current.length)
          ws.send(JSON.stringify({ type: "watch", symbols: watchRef.current }));
      };
      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.type !== "desk") return;
        if (msg.positions !== undefined) setPositions(msg.positions);
        if (msg.orders !== undefined) setOrders(msg.orders);
        if (msg.summary !== undefined) setSummary(msg.summary);
        if (msg.status !== undefined) setStatus(msg.status);
        if (msg.ticks !== undefined) setTicks(msg.ticks);
        setUpdatedAt(Date.now());
      };
      ws.onclose = () => {
        if (wsRef.current && wsRef.current !== ws) return; // a stale socket closing late
        setLive(false);
        wsRef.current = null;
        if (closed) return;
        startPoll();
        retryRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retryRef.current);
      stopPoll();
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [loadLive, loadStatic]);

  /* After a trade action the socket already holds the truth — ask it for a frame right now
     instead of re-fetching four endpoints (each a round trip to the trader's MT5). */
  const kick = useCallback(() => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "sync" }));
      return;
    }
    loadLive(true);
  }, [loadLive]);

  /* Optimistic edits: the row changes on tap, the next frame confirms it. */
  const patchPosition = useCallback((ticket, patch) => {
    setPositions((rows) => rows.map((p) => (p.ticket === ticket ? { ...p, ...patch } : p)));
  }, []);
  const dropPosition = useCallback((ticket) => {
    setPositions((rows) => rows.filter((p) => p.ticket !== ticket));
  }, []);
  const dropOrder = useCallback((ticket) => {
    setOrders((rows) => rows.filter((o) => o.ticket !== ticket));
  }, []);

  const blocked = [];
  if (summary?.day_stopped)
    blocked.push(
      summary.day_stop_reason === "auto"
        ? "Daily loss limit hit — your trading day was closed automatically."
        : "You closed your trading day — resume it to open new trades."
    );
  if (summary?.kill_switch) blocked.push("Kill Switch is ACTIVE — new trades blocked.");
  if (summary?.loss_limit_hit && !summary?.day_stopped)
    blocked.push("Daily loss limit hit — trading blocked for today.");
  if (summary?.trade_limit_hit)
    blocked.push(
      `Daily trade limit reached (${summary.trades_used_today}/${summary.max_trades_per_day}) — no new trades or orders today.`
    );
  if (summary?.profit_target_hit)
    blocked.push(
      `Daily profit target of $${Number(summary.daily_profit_target).toFixed(2)} reached — new trades are blocked for today.`
    );
  if (summary && !summary.within_window)
    blocked.push(`Outside trading window (${summary.window}) — new entries only; open trades keep running.`);
  if (status && !status.connected) blocked.push("MT5 is not connected.");

  return {
    status,
    settings,
    symbols,
    positions,
    orders,
    summary,
    ticks,
    live,
    watch,
    refreshing,
    updatedAt,
    blocked,
    kick,
    reload: loadLive,
    patchPosition,
    dropPosition,
    dropOrder,
    loadStatic,
  };
};
